import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@base-ui/react/dialog";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { api } from "../../api";
import { LoadingLines } from "../../Loading";
import { useWorkspaceScope } from "../../workspace-queries";
import { RunSquares } from "./RunSquares";
import { when } from "./format";

type Result =
  | { kind: "method"; id: string; title: string; detail: string | null }
  | { kind: "run"; id: string; status: string; title: string; detail: string }
  | { kind: "proposal"; id: string; methodId: string; title: string; detail: string };
const SECTIONS: [Result["kind"], string][] = [["method", "Methods"], ["run", "Runs"], ["proposal", "Changes"]];

/** One search for Methods, runs (by name, inputs and result) and changes. Cmd/Ctrl+K opens it. */
export function SearchDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const scope = useWorkspaceScope();
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [cursor, setActive] = useState(0);
  useEffect(() => { const timer = setTimeout(() => setQuery(text.trim()), 200); return () => clearTimeout(timer); }, [text]);
  useEffect(() => { if (!open) { setText(""); setQuery(""); } }, [open]);
  const search = useQuery({ queryKey: ["search", scope, query], queryFn: () => api.search(query), enabled: open && !!query, placeholderData: previous => previous });
  const results = useMemo<Result[]>(() => {
    const data = query ? search.data : undefined;
    if (!data) return [];
    return [
      ...data.methods.map(m => ({ kind: "method" as const, id: m.id, title: m.name, detail: m.goal })),
      ...data.runs.map(r => ({ kind: "run" as const, id: r.id, status: r.status, title: r.label || r.method_name,
        detail: [r.label ? r.method_name : null, when(r.created_at), r.match].filter(Boolean).join(" · ") })),
      ...data.proposals.map(p => ({ kind: "proposal" as const, id: p.id, methodId: p.method_id, title: p.cause, detail: `${p.method_name} · change ${p.status}` })),
    ];
  }, [search.data, query]);
  const active = Math.max(0, Math.min(cursor, results.length - 1));
  const close = () => onOpenChange(false);
  function go(result: Result) {
    close();
    if (result.kind === "method") void navigate({ to: "/methods/$workflowId", params: { workflowId: result.id } });
    else if (result.kind === "run") void navigate({ to: "/runs/$runId", params: { runId: result.id } });
    else void navigate({ to: "/methods/$workflowId/proposals/$proposalId", params: { workflowId: result.methodId, proposalId: result.id } });
  }
  const link = (result: Result, index: number) => {
    const props = { id: `search-result-${index}`, role: "option", "aria-selected": index === active, className: "search-result",
      onClick: close, onMouseMove: () => setActive(index) };
    const body = <>{result.kind === "run" && <RunSquares statuses={[result.status]} slots={1} size="mark" />}
      <span><strong>{result.title}</strong>{result.detail && <small>{result.detail}</small>}</span></>;
    return result.kind === "method" ? <Link key={`m:${result.id}`} {...props} to="/methods/$workflowId" params={{ workflowId: result.id }}>{body}</Link>
      : result.kind === "run" ? <Link key={`r:${result.id}`} {...props} to="/runs/$runId" params={{ runId: result.id }}>{body}</Link>
      : <Link key={`p:${result.id}`} {...props} to="/methods/$workflowId/proposals/$proposalId" params={{ workflowId: result.methodId, proposalId: result.id }}>{body}</Link>;
  };
  return <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Backdrop className="dash-backdrop" />
      <Dialog.Popup className="dash-dialog search-dialog">
        <Dialog.Title className="visually-hidden">Search</Dialog.Title>
        <label className="list-search search-input">
          <MagnifyingGlassIcon size={16} />
          <input autoFocus value={text} placeholder="Search Methods, runs and changes" aria-label="Search"
            role="combobox" aria-expanded={results.length > 0} aria-controls="search-results"
            aria-activedescendant={results.length ? `search-result-${active}` : undefined}
            onChange={event => { setText(event.target.value); setActive(0); }}
            onKeyDown={event => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                if (results.length) setActive((active + (event.key === "ArrowDown" ? 1 : -1) + results.length) % results.length);
              } else if (event.key === "Enter" && results[active]) {
                event.preventDefault();
                go(results[active]);
              }
            }} />
        </label>
        <div id="search-results" role="listbox" aria-label="Search results" className="search-results">
          {!query ? <p className="search-note">Type a name, an input, or words from a result.</p>
            : search.isPending ? <LoadingLines rows={3} label="Searching" />
            : search.isError ? <p className="search-note" role="alert">Search did not work. Please try again.</p>
            : !results.length ? <p className="search-note">Nothing matches “{query}”.</p>
            : SECTIONS.map(([kind, label]) => {
              const items = results.map((result, index) => [result, index] as const).filter(([result]) => result.kind === kind);
              return items.length ? <div key={kind} role="group" aria-label={label}><span className="search-label">{label}</span>{items.map(([result, index]) => link(result, index))}</div> : null;
            })}
        </div>
      </Dialog.Popup>
    </Dialog.Portal>
  </Dialog.Root>;
}
